import { RouteObject } from "react-router-dom";
import { lazy } from "react";

// Lazy load components
const LandingPage = lazy(() => import("../pages/landing/page"));
const HomePage = lazy(() => import("../pages/home/page"));
const DashboardLayout = lazy(() => import("../pages/dashboard/layout"));
const DashboardPage = lazy(() => import("../pages/dashboard/page"));
const AirtimePage = lazy(() => import("../pages/airtime/page"));
const DataPage = lazy(() => import("../pages/data/page"));
const ElectricityPage = lazy(() => import("../pages/electricity/page"));
const CableTVPage = lazy(() => import("../pages/cable-tv/page"));
const ExamPinPage = lazy(() => import("../pages/exam-pin/page"));
const EpinPage = lazy(() => import("../pages/epin/page"));
const LoanPage = lazy(() => import("../pages/loan/page"));
const TopupPage = lazy(() => import("../pages/topup/page"));
const TransactionsPage = lazy(() => import("../pages/transactions/page"));
const ProfilePage = lazy(() => import("../pages/profile/page"));
const EditProfilePage = lazy(() => import("../pages/profile/edit/page"));
const ChangePasswordPage = lazy(
  () => import("../pages/profile/change-password/page"),
);
const ChangePinPage = lazy(() => import("../pages/profile/change-pin/page"));
const CreatePinPage = lazy(() => import("../pages/profile/create-pin/page"));
const SettingsPage = lazy(() => import("../pages/settings/page"));
const NotificationsPage = lazy(() => import("../pages/notifications/page"));
const ReferralPage = lazy(() => import("../pages/referral/page"));
const NotFoundPage = lazy(() => import("../pages/NotFound"));

// Auth pages
const SignupPage = lazy(() => import("../pages/auth/signup/page"));
const LoginPage = lazy(() => import("../pages/auth/login/page"));
const VerifyEmailPage = lazy(() => import("../pages/auth/verify-email/page"));
const ForgotPasswordPage = lazy(
  () => import("../pages/auth/forgot-password/page"),
);
const ResetPasswordPage = lazy(
  () => import("../pages/auth/reset-password/page"),
);

// Mobile pages
const MobileOnboardingPage = lazy(
  () => import("../pages/mobile/onboarding/page"),
);
const MobilePinLoginPage = lazy(
  () => import("../pages/mobile/auth/pin-login/page"),
);
const MobileSetPinPage = lazy(
  () => import("../pages/mobile/auth/set-pin/page"),
);

import AdminLayout from "../pages/admin/layout";
import AdminDashboard from "../pages/admin/dashboard/page";
import AdminUsers from "../pages/admin/users/page";
import AdminTransactions from "../pages/admin/transactions/page";
import AdminNetworks from "../pages/admin/networks/page";
import AdminDataPlans from "../pages/admin/data-plans/page";
import AdminElectricity from "../pages/admin/electricity/page";
import AdminCableTV from "../pages/admin/cable-tv/page";
import AdminExamPins from "../pages/admin/exam-pins/page";
import AdminAPIProviders from "../pages/admin/api-providers/page";
import AdminLoans from "../pages/admin/loans/page";
import AdminLoanSettings from "../pages/admin/loan-settings/page";
import AdminSettings from "../pages/admin/settings/page";

const routes: RouteObject[] = [
  {
    path: "/",
    element: <LandingPage />,
  },
  {
    path: "/home",
    element: <HomePage />,
  },
  {
    path: "/dashboard",
    element: <DashboardLayout />,
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      // Add more child routes here as needed
      {
        path: "/dashboard/airtime",
        element: <AirtimePage />,
      },
      {
        path: "/dashboard/data",
        element: <DataPage />,
      },
      {
        path: "/dashboard/electricity",
        element: <ElectricityPage />,
      },
      {
        path: "/dashboard/cable-tv",
        element: <CableTVPage />,
      },
      {
        path: "/dashboard/exam-pin",
        element: <ExamPinPage />,
      },
      {
        path: "/dashboard/epin",
        element: <EpinPage />,
      },
      {
        path: "/dashboard/topup",
        element: <TopupPage />,
      },
      {
        path: "/dashboard/loan",
        element: <LoanPage />,
      },
      {
        path: "/dashboard/transactions",
        element: <TransactionsPage />,
      },
      {
        path: "/dashboard/profile",
        element: <ProfilePage />,
      },
      {
        path: "/dashboard/profile/edit",
        element: <EditProfilePage />,
      },
      {
        path: "/dashboard/profile/change-password",
        element: <ChangePasswordPage />,
      },
      {
        path: "/dashboard/profile/change-pin",
        element: <ChangePinPage />,
      },
      {
        path: "/dashboard/profile/create-pin",
        element: <CreatePinPage />,
      },
      {
        path: "/dashboard/settings",
        element: <SettingsPage />,
      },
      {
        path: "/dashboard/notifications",
        element: <NotificationsPage />,
      },
      {
        path: "/dashboard/referral",
        element: <ReferralPage />,
      },
    ],
  },

  // Auth routes
  {
    path: "/auth/signup",
    element: <SignupPage />,
  },
  {
    path: "/auth/login",
    element: <LoginPage />,
  },
  {
    path: "/auth/verify-email",
    element: <VerifyEmailPage />,
  },
  {
    path: "/auth/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/auth/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "*",
    element: <NotFoundPage />,
  },
  // Mobile Routes
  {
    path: "/mobile/onboarding",
    element: <MobileOnboardingPage />,
  },
  {
    path: "/mobile/pin-login",
    element: <MobilePinLoginPage />,
  },
  {
    path: "/mobile/set-pin",
    element: <MobileSetPinPage />,
  },
  // Admin Routes
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      {
        path: "users",
        element: <AdminUsers />,
      },
      {
        path: "transactions",
        element: <AdminTransactions />,
      },
      {
        path: "networks",
        element: <AdminNetworks />,
      },
      {
        path: "data-plans",
        element: <AdminDataPlans />,
      },
      {
        path: "electricity",
        element: <AdminElectricity />,
      },
      {
        path: "cable-tv",
        element: <AdminCableTV />,
      },
      {
        path: "exam-pins",
        element: <AdminExamPins />,
      },
      {
        path: "api-providers",
        element: <AdminAPIProviders />,
      },
      {
        path: "loans",
        element: <AdminLoans />,
      },
      {
        path: "loan-settings",
        element: <AdminLoanSettings />,
      },
      {
        path: "settings",
        element: <AdminSettings />,
      },
    ],
  },
];

export default routes;
